import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ProductCard from '../components/ProductCard';
import { Product } from '../types/Product';
import { products } from '../data/products';

const CataloguePage: React.FC = () => {
  const [filteredProducts, setFilteredProducts] = useState<Product[]>(products);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 30000]);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);

  // Extraire toutes les catégories uniques des produits
  const categories = Array.from(new Set(products.map(product => product.category)));

  // Filtrage des produits en fonction des critères sélectionnés
  useEffect(() => {
    let result = [...products];
    
    // Filtre par catégorie
    if (selectedCategory) {
      result = result.filter(product => product.category === selectedCategory);
    }
    
    // Filtre par recherche
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        product => 
          product.name.toLowerCase().includes(query) || 
          product.description.toLowerCase().includes(query) ||
          product.seller.name.toLowerCase().includes(query)
      );
    }
    
    // Filtre par prix
    result = result.filter(
      product => product.price >= priceRange[0] && product.price <= priceRange[1]
    );
    
    setFilteredProducts(result);
  }, [selectedCategory, searchQuery, priceRange]);

  // Animation pour les cartes de produits
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen pt-20 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <motion.h1 
            className="text-4xl font-extrabold text-gray-900 sm:text-5xl"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            Catalogue des produits
          </motion.h1>
          <motion.p 
            className="mt-4 max-w-2xl mx-auto text-xl text-gray-500"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            Découvrez notre sélection de produits de qualité disponibles pour le commerce transfrontalier.
          </motion.p>
        </div>

        {/* Barre de recherche */}
        <div className="mb-8">
          <div className="max-w-3xl mx-auto">
            <motion.div 
              className="flex w-full rounded-lg shadow-sm overflow-hidden"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Rechercher un produit, un fournisseur..."
                className="flex-1 px-6 py-4 border-0 focus:ring-0 focus:outline-none"
              />
              <button className="bg-indigo-600 px-6 text-white font-medium hover:bg-indigo-700 transition-colors">
                Rechercher
              </button>
            </motion.div>
          </div>
        </div>

        <div className="lg:grid lg:grid-cols-4 lg:gap-x-6">
          {/* Filtres pour écrans larges */}
          <motion.div 
            className="hidden lg:block col-span-1"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Filtres</h2>
              
              {/* Filtre par catégorie */}
              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-900 mb-2">Catégorie</h3>
                <div className="space-y-2">
                  <div className="flex items-center">
                    <input
                      id="all-categories"
                      name="category"
                      type="radio"
                      checked={selectedCategory === ''}
                      onChange={() => setSelectedCategory('')}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <label htmlFor="all-categories" className="ml-3 text-sm text-gray-700">
                      Toutes les catégories
                    </label>
                  </div>
                  
                  {categories.map((category) => (
                    <div key={category} className="flex items-center">
                      <input
                        id={`category-${category}`}
                        name="category"
                        type="radio"
                        checked={selectedCategory === category}
                        onChange={() => setSelectedCategory(category)}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <label htmlFor={`category-${category}`} className="ml-3 text-sm text-gray-700">
                        {category}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Filtre par prix */}
              <div>
                <h3 className="text-sm font-medium text-gray-900 mb-2">Prix (€)</h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">{priceRange[0]} €</span>
                    <span className="text-sm text-gray-500">{priceRange[1]} €</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="30000"
                    step="1000"
                    value={priceRange[1]}
                    onChange={(e) => setPriceRange([priceRange[0], parseInt(e.target.value)])}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  />
                </div>
              </div>
              
              {/* Bouton pour réinitialiser les filtres */}
              <div className="mt-6">
                <button 
                  onClick={() => {
                    setSelectedCategory('');
                    setPriceRange([0, 30000]);
                    setSearchQuery('');
                  }}
                  className="w-full py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Réinitialiser les filtres
                </button>
              </div>
            </div>
          </motion.div>
          
          {/* Bouton pour ouvrir les filtres sur mobile */}
          <div className="lg:hidden mb-4">
            <button
              onClick={() => setIsFiltersOpen(!isFiltersOpen)}
              className="w-full flex justify-between items-center px-4 py-3 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <span>Filtres{selectedCategory && ` (${selectedCategory})`}</span>
              <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                {isFiltersOpen ? (
                  <path fillRule="evenodd" clipRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" />
                ) : (
                  <path fillRule="evenodd" clipRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
                )}
              </svg>
            </button>
            
            {/* Panneau de filtres mobile */}
            <AnimatePresence>
              {isFiltersOpen && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mt-2 bg-white rounded-md shadow-lg p-4 overflow-hidden"
                >
                  {/* Contenu du panneau de filtres mobile (même contenu que pour les écrans larges) */}
                  <div className="mb-6">
                    <h3 className="text-sm font-medium text-gray-900 mb-2">Catégorie</h3>
                    <div className="space-y-2">
                      <div className="flex items-center">
                        <input
                          id="mobile-all-categories"
                          name="mobile-category"
                          type="radio"
                          checked={selectedCategory === ''}
                          onChange={() => setSelectedCategory('')}
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        />
                        <label htmlFor="mobile-all-categories" className="ml-3 text-sm text-gray-700">
                          Toutes les catégories
                        </label>
                      </div>
                      
                      {categories.map((category) => (
                        <div key={`mobile-${category}`} className="flex items-center">
                          <input
                            id={`mobile-category-${category}`}
                            name="mobile-category"
                            type="radio"
                            checked={selectedCategory === category}
                            onChange={() => setSelectedCategory(category)}
                            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                          />
                          <label htmlFor={`mobile-category-${category}`} className="ml-3 text-sm text-gray-700">
                            {category}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Filtre par prix */}
                  <div>
                    <h3 className="text-sm font-medium text-gray-900 mb-2">Prix (€)</h3>
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-500">{priceRange[0]} €</span>
                        <span className="text-sm text-gray-500">{priceRange[1]} €</span>
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="30000"
                        step="1000"
                        value={priceRange[1]}
                        onChange={(e) => setPriceRange([priceRange[0], parseInt(e.target.value)])}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                      />
                    </div>
                  </div>
                  
                  {/* Bouton pour réinitialiser les filtres */}
                  <div className="mt-6">
                    <button 
                      onClick={() => {
                        setSelectedCategory('');
                        setPriceRange([0, 30000]);
                        setSearchQuery('');
                      }}
                      className="w-full py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      Réinitialiser les filtres
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          
          {/* Liste des produits */}
          <div className="col-span-3">
            {filteredProducts.length === 0 ? (
              <div className="bg-white rounded-lg shadow p-6 text-center">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h3 className="mt-2 text-lg font-medium text-gray-900">Aucun produit trouvé</h3>
                <p className="mt-1 text-sm text-gray-500">Essayez de modifier vos critères de recherche.</p>
              </div>
            ) : (
              <motion.div 
                className="grid grid-cols-1 gap-y-10 gap-x-6 sm:grid-cols-2 lg:grid-cols-3"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
              >
                {filteredProducts.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CataloguePage;
